import { Component, computed, effect, inject, OnInit, ViewChild } from '@angular/core';
import { PaaService } from '../paa.service';
import { PaaRepository } from '@/store/paa-state/paa-state.repository';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DataTableAction, DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { Paa, TasksStatus } from '@/types';
import { getPaaCurrentPlansDataTableStructure } from './utils/paa-current-plans.data-table-object';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { getPaaDeleteObject } from './utils/paa-delete-dialog-object';
import { AuthorizationService } from '@/services/authorization.service';
import { createServerQuery } from '@/components/data-table-builder/server-query';

@Component({
  selector: 'sm-current-plans',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DialogComponent],
  templateUrl: './current-plans.component.html',
  styleUrl: './current-plans.component.scss',
})
export class PaaCurrentPlansComponent implements OnInit {
  paaService = inject(PaaService);
  paaRepository = inject(PaaRepository);
  router = inject(Router);
  messageService = inject(MessageService);
  authService = inject(AuthorizationService);

  breadcrumb = [
    {
      label: 'paa.pages.current_plans',
      routerLink: ['/paa-current-plans'],
    },
  ];

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  paaDialog?: DialogObject;

  serverQuery = createServerQuery({});
  queryParams = computed(() => ({
    page: this.serverQuery.page(),
    pageSize: this.serverQuery.pageSize(),
    sortBy: this.serverQuery.sortBy(),
    sortOrder: this.serverQuery.sortOrder(),
    search: this.serverQuery.search(),
  }));

  rowActionsFn: (target: Paa) => DataTableRowActions = (target: Paa) => {
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};
    if (target.processInstanceId && target.status != TasksStatus.PUBLIEE)
      additionalActions['workflow'] = [
        {
          icon: 'pi pi-check',
          label: 'paa.actions.treat',
          action: () => {
            this.router.navigate([`paa-edit-plan`, target.id]);
          },
        },
      ];
    additionalActions['history'] = [
      {
        icon: 'pi pi-search',
        label: 'paa.actions.access_history',
        action: () => {
          this.router.navigate([`paa-version-history`, target.id]);
        },
      },
    ];
    if (target.processInstanceId)
      additionalActions['history'].push({
        icon: 'pi pi-search',
        label: 'paa.actions.access_task_history',
        action: () => {
          this.router.navigate([`paa-task-history`, target.processInstanceId]);
        },
      });
    return {
      defaultActions: {
        inspect: {
          label: 'paa.actions.inspect',
        },
        edit: {
          label: 'paa.actions.modify',
        },
        delete: {
          label: 'paa.actions.delete',
        },
      },
      inspectAction: (row: Paa) => {
        this.router.navigate([`/paa-inspect-plan`, row.id]);
      },
      editAction:
        target.status === TasksStatus.BROUILLON && this.authService.isRedacteur()
          ? (row: Paa) => {
              this.router.navigate([`/paa-edit-plan`, row.id]);
            }
          : undefined,
      deleteAction:
        target.status === TasksStatus.BROUILLON && this.authService.isRedacteur()
          ? (row: Paa) => {
              this.paaDialog = getPaaDeleteObject({
                store: this.paaRepository,
                representation: row.ref,
                action: () => {
                  this.deletePaa(row.id);
                  this.dialogComponent.toggleVisible();
                },
                toggleVisiblity: () => this.dialogComponent.toggleVisible(),
              });
              this.dialogComponent.toggleVisible();
            }
          : undefined,
      additionalActions,
    };
  };

  data: Paa[] = [];
  totalRecords = 0;
  datatableObject: DynamicDataTable = getPaaCurrentPlansDataTableStructure({
    store: this.paaRepository,
    rowActionsFn: this.rowActionsFn,
    createAction: this.authService.isRedacteur()
      ? () => {
          this.router.navigate(['/paa-new-plan']);
        }
      : undefined,
    serverQuery: this.serverQuery,
  });

  loading = true;

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.search();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  redirectUpdatePaa() {
    this.router.navigate(['/paa-edit-plan']);
  }

  loadData() {
    this.paaService
      .getCurrentUserPaa({
        page: this.serverQuery.page(),
        size: this.serverQuery.pageSize(),
        sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : '',
        search: this.serverQuery.search(),
      })
      .subscribe({
        next: data => {
          this.data = data.content;
          this.totalRecords = data.totalElements;
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || 'Une erreur est survenue lors de la récupération des paas',
          });
        },
      });
  }

  deletePaa(id: number) {
    return this.paaService.deletePaa(id).subscribe({
      next: () => {
        this.loadData();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le paa a été supprimé avec succès',
        });
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || 'Une erreur est survenue lors de la suppression du paa',
        });
      },
    });
  }
}
